// Marking of the ÖSD B2 Hören Teil 2 information-transfer table.
//
// Like the printed ÖSD sheet, every cell is blank (only fixed start times are
// printed). Each ROW is one item worth 1 point, split evenly over its cells, so the
// ten rows make the part's 10 points. A row is stored in part.questions as:
//   { questionId, questionNumber, kind: 'row', row, statement, correctAnswer (summary),
//     cells: [{ col, field?, kind, correctAnswer, accept? }] }
// and the student's answer for it is an object keyed by cell: { col1: '240', 'col3|to': '18' }.
//
// Cell kinds:
//   'number'   — "240", "7/6"; every number must match in order ("240 €", "1.450", "7 Tage, 6 Nächte" ok)
//   'time'     — an hour: "21", "21:00", "21.00 Uhr"
//   'checkbox' — the exact set of options to tick
//   'text'     — one of correctAnswer/accept, case- and space-insensitive
//
// examService (exam score), practiceGrading (practice score) and ExamResultsPage
// (review) all mark through here so the three can never disagree.

export const isTableGapQuestion = (question) => question?.kind === 'row' && Array.isArray(question.cells);

export const cellKey = (cell) => (cell.field ? `${cell.col}|${cell.field}` : cell.col);

const numbersIn = (value) =>
  String(value ?? '')
    // German thousands separator: "1.450" / "1 450" → "1450"
    .replace(/(\d)[.\s](?=\d{3}(?!\d))/g, '$1')
    .replace(/(\d),(\d)/g, '$1.$2')
    .match(/\d+(?:\.\d+)?/g)
    ?.map(Number) || [];

const hourOf = (value) => {
  const m = String(value ?? '').match(/\d{1,2}/);
  return m ? Number(m[0]) : null;
};

const optionSet = (value) =>
  (Array.isArray(value) ? value : value == null || value === '' ? [] : [value])
    .map((v) => String(v).trim())
    .filter(Boolean)
    .sort();

const squash = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const isEmpty = (value) =>
  value == null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);

export function isCellCorrect(cell, value) {
  if (isEmpty(value)) return false;
  switch (cell.kind) {
    case 'number': {
      const want = numbersIn(cell.correctAnswer);
      const got = numbersIn(value);
      return want.length > 0 && want.length === got.length && want.every((n, i) => n === got[i]);
    }
    case 'time':
      return hourOf(value) !== null && hourOf(value) === hourOf(cell.correctAnswer);
    case 'checkbox': {
      const want = optionSet(cell.correctAnswer);
      const got = optionSet(value);
      return want.length === got.length && want.every((o, i) => o === got[i]);
    }
    default:
      return [cell.correctAnswer, ...(cell.accept || [])].some((a) => squash(a) === squash(value));
  }
}

/** Share of the row's point earned: correct cells / all cells (0..1). */
export function tableRowCredit(question, answer) {
  if (!isTableGapQuestion(question) || !question.cells.length) return 0;
  const values = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {};
  const right = question.cells.filter((cell) => isCellCorrect(cell, values[cellKey(cell)])).length;
  return right / question.cells.length;
}

/**
 * ÖSD "Messpunkte" of one row (Modellsatz B2, Hören Aufgabe 2): a written cell
 * is 1 or 0; a tick cell is judged option by option against the row's options
 * — all right 2, at least half right 1, else 0. A tick cell left empty, or with
 * every option ticked (unless that is the key), scores 0.
 * `options`: the row's tick options (part.table.rows[].options).
 */
export function rowMesspunkte(question, answer, options = []) {
  if (!isTableGapQuestion(question)) return 0;
  const values = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {};
  return question.cells.reduce((sum, cell) => {
    const value = values[cellKey(cell)];
    if (cell.kind !== 'checkbox') return sum + (isCellCorrect(cell, value) ? 1 : 0);
    const want = optionSet(cell.correctAnswer);
    const got = optionSet(value);
    const all = options.length ? options.map(String) : [...new Set([...want, ...got])];
    if (!got.length || (all.length > 1 && got.length === all.length && want.length !== all.length)) return sum;
    const right = all.filter((o) => want.includes(o) === got.includes(o)).length;
    return sum + (right === all.length ? 2 : right * 2 >= all.length ? 1 : 0);
  }, 0);
}

/** The most Messpunkte a row can give: 1 per written cell, 2 per tick cell. */
export const rowMaxMesspunkte = (question) =>
  (isTableGapQuestion(question) ? question.cells.reduce((sum, cell) => sum + (cell.kind === 'checkbox' ? 2 : 1), 0) : 0);

/** A row counts as fully correct only when every cell is right (used for ✓/✗ badges). */
export const isTableGapCorrect = (question, answer) => tableRowCredit(question, answer) === 1;

export const hasTableAnswer = (answer) =>
  Boolean(answer && typeof answer === 'object' && Object.values(answer).some((v) => !isEmpty(v)));

const formatValue = (cell, value) => {
  if (isEmpty(value)) return '–';
  if (Array.isArray(value)) return value.join(', ');
  return cell.kind === 'time' ? `${String(value).trim()} Uhr` : String(value).trim();
};

/** Human-readable form of a row answer (or of a single cell's correct answer). */
export function formatTableGapAnswer(question, value) {
  if (!isTableGapQuestion(question)) return Array.isArray(value) ? value.join(', ') : String(value ?? '');
  const values = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return question.cells.map((cell) => formatValue(cell, values[cellKey(cell)])).join(' · ');
}

export const formatCellAnswer = (cell) => formatValue(cell, cell.correctAnswer);
